import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './Wishlist.module.css';
import { wishlistApi, searchApi, isbnApi, shopsApi, getMediaUrl, libraryApi } from '../api';

const SORT_OPTION_KEYS = [
  { value: 'date-desc', labelKey: 'wishlist.sortDateDesc' },
  { value: 'date-asc', labelKey: 'wishlist.sortDateAsc' },
  { value: 'title-asc', labelKey: 'wishlist.sortTitleAsc' },
  { value: 'title-desc', labelKey: 'wishlist.sortTitleDesc' },
  { value: 'band-asc', labelKey: 'wishlist.sortBandAsc' },
  { value: 'band-desc', labelKey: 'wishlist.sortBandDesc' },
];

const sortWishlistItems = (items, sortBy) => {
  const sorted = [...items];
  switch (sortBy) {
    case 'title-asc':
      return sorted.sort((a, b) => (a.manga_title || '').localeCompare(b.manga_title || ''));
    case 'title-desc':
      return sorted.sort((a, b) => (b.manga_title || '').localeCompare(a.manga_title || ''));
    case 'band-asc':
      return sorted.sort((a, b) => (a.band_number ?? Infinity) - (b.band_number ?? Infinity));
    case 'band-desc':
      return sorted.sort((a, b) => (b.band_number ?? -Infinity) - (a.band_number ?? -Infinity));
    case 'date-asc':
      return sorted.sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0));
    case 'date-desc':
    default:
      return sorted.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
  }
};

// Helper-Funktion zum Neuladen der Wishlist
const reloadWishlist = async (setWishlistItems) => {
  const result = await wishlistApi.getWishlist();
  if (result.success) {
    setWishlistItems(result.data || []);
  }
};

// userId wird nicht mehr benötigt (Session-basierte Auth)
function Wishlist() {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [wishlistItems, setWishlistItems] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isReloading, setIsReloading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [message, setMessage] = useState(null);

  // Band-Auswahl in Suchergebnissen
  const [expandedResultId, setExpandedResultId] = useState(null);
  const [bandNumberInputs, setBandNumberInputs] = useState({}); // mangaId -> string
  
  // Modal für manuelles Hinzufügen
  const [showAddModal, setShowAddModal] = useState(false);
  const [addMode, setAddMode] = useState('isbn'); // 'isbn' oder 'title'
  const [isbnInput, setIsbnInput] = useState('');
  const [titleInput, setTitleInput] = useState('');
  const [bandInput, setBandInput] = useState('');

  // Shop-Links pro Band (band_id -> { loading, loaded, data, error })
  const [shopLinksByBand, setShopLinksByBand] = useState({});

  // Ansicht (Grid/Liste) + Sortierung, persistiert für wiederkehrende Besuche
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('wishlistViewMode') || 'list');
  const [sortBy, setSortBy] = useState(() => localStorage.getItem('wishlistSortBy') || 'date-desc');
  const [filterMangaTitle, setFilterMangaTitle] = useState('');

  useEffect(() => { localStorage.setItem('wishlistViewMode', viewMode); }, [viewMode]);
  useEffect(() => { localStorage.setItem('wishlistSortBy', sortBy); }, [sortBy]);

  const filterOptions = useMemo(() => {
    const titles = new Set(wishlistItems.map(item => item.manga_title).filter(Boolean));
    return Array.from(titles).sort((a, b) => a.localeCompare(b));
  }, [wishlistItems]);

  useEffect(() => {
    if (filterMangaTitle && !filterOptions.includes(filterMangaTitle)) {
      setFilterMangaTitle('');
    }
  }, [filterOptions, filterMangaTitle]);

  const filteredWishlistItems = useMemo(
    () => filterMangaTitle
      ? wishlistItems.filter(item => item.manga_title === filterMangaTitle)
      : wishlistItems,
    [wishlistItems, filterMangaTitle]
  );

  const sortedWishlistItems = useMemo(
    () => sortWishlistItems(filteredWishlistItems, sortBy),
    [filteredWishlistItems, sortBy]
  );

  // Wishlist beim Laden abrufen
  useEffect(() => {
    const fetchWishlist = async () => {
      try {
        const result = await wishlistApi.getWishlist();
        if (result.success) {
          // API v1.2: data ist direkt das Array
          setWishlistItems(result.data || []);
        } else {
          console.error('Wishlist Fehler:', result.error);
          setWishlistItems([]);
        }
      } catch (error) {
        console.error('Fehler beim Laden der Wishlist:', error);
        if (!error.message?.includes('nicht gefunden')) {
          setMessage({ type: 'error', text: t('wishlist.loadError') });
        }
        setWishlistItems([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchWishlist();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Message nach 5 Sekunden ausblenden
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  // Shop-Links für Bände mit band_id laden (nur noch nicht geladene)
  useEffect(() => {
    const bandIds = wishlistItems
      .filter(item => item.band_id != null)
      .map(item => item.band_id);
    const toFetch = bandIds.filter(id => !shopLinksByBand[id]);
    if (toFetch.length === 0) return;

    setShopLinksByBand(prev => {
      const next = { ...prev };
      toFetch.forEach(id => { next[id] = { loading: true }; });
      return next;
    });

    toFetch.forEach(async (bandId) => {
      try {
        const result = await shopsApi.getShopsForBand(bandId);
        setShopLinksByBand(prev => ({
          ...prev,
          [bandId]: result.success
            ? { loading: false, loaded: true, data: result.data || [] }
            : { loading: false, loaded: true, data: [], error: result.error?.message }
        }));
      } catch (e) {
        setShopLinksByBand(prev => ({ ...prev, [bandId]: { loading: false, loaded: true, data: [], error: e.message } }));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wishlistItems]);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchTerm.trim()) return;

    setIsSearching(true);
    setMessage(null);

    try {
      const result = await searchApi.searchMangas(searchTerm, { limit: 50 });
      if (result.success) {
        const items = result.data?.items || [];
        setSearchResults(items);
        if (items.length === 0) {
          setMessage({ type: 'info', text: t('wishlist.noResults') });
        }
      } else {
        setMessage({ type: 'error', text: t('wishlist.searchError') });
        setSearchResults([]);
      }
    } catch (error) {
      setMessage({ type: 'error', text: t('wishlist.searchError') });
    } finally {
      setIsSearching(false);
    }
  };

  const handleAddToWishlist = async (manga) => {
    setIsAdding(true);
    try {
      const mangaId = manga.manga_id || manga.mangaId || manga.id;
      const result = await wishlistApi.addManga(mangaId);
      
      if (result.success) {
        setMessage({ type: 'success', text: t('wishlist.addedToWishlist', { title: manga.title || manga.name }) });
        // Wishlist neu laden
        const wishlistResult = await wishlistApi.getWishlist();
        if (wishlistResult.success) {
          setWishlistItems(wishlistResult.data || []);
        }
        // Aus Suchergebnissen entfernen
        setSearchResults(searchResults.filter(m => (m.manga_id || m.id) !== mangaId));
      } else {
        setMessage({ type: 'warning', text: result.error?.message || t('wishlist.addError') });
      }
    } catch (error) {
      setMessage({ type: 'error', text: t('wishlist.addError') });
    } finally {
      setIsAdding(false);
    }
  };

  // Spezifischen Band zur Wishlist hinzufügen
  // Versucht band_id über Library-Bände zu ermitteln; fällt auf manga-only zurück wenn nicht möglich
  const handleAddBandToWishlist = async (manga, bandNumberStr) => {
    const mangaId = manga.manga_id || manga.mangaId || manga.id;
    const bandNumber = parseInt(bandNumberStr, 10);
    if (!bandNumber || bandNumber < 0) {
      setMessage({ type: 'warning', text: t('wishlist.invalidBandNumber') });
      return;
    }
    setIsAdding(true);
    try {
      // Versuche band_id über Library-Bände zu holen (nur vorhanden wenn Manga in Library)
      const bandsResult = await libraryApi.getAllBands(mangaId);
      const ownedBands = bandsResult.success ? (bandsResult.items || []) : [];
      const matchedBand = ownedBands.find(b => b.band_number === bandNumber);

      let result;
      if (matchedBand?.band_id) {
        // Band ist bereits im Besitz des Users → trotzdem zur Wishlist (z.B. als Geschenk-Wunsch)
        result = await wishlistApi.addBand(mangaId, matchedBand.band_id);
      } else {
        // band_id nicht verfügbar (Band nicht in Library oder Manga nicht in Library)
        // Fallback: manga-only Eintrag mit Hinweis
        result = await wishlistApi.addManga(mangaId);
        if (result.success) {
          setMessage({
            type: 'info',
            text: t('wishlist.addedWithBandFallback', { title: manga.title, band: bandNumber })
          });
          await reloadWishlist(setWishlistItems);
          setExpandedResultId(null);
          setBandNumberInputs(prev => ({ ...prev, [mangaId]: '' }));
          return;
        }
      }

      if (result.success) {
        setMessage({ type: 'success', text: t('wishlist.addedTitleBand', { title: manga.title, band: bandNumber }) });
        await reloadWishlist(setWishlistItems);
        setExpandedResultId(null);
        setBandNumberInputs(prev => ({ ...prev, [mangaId]: '' }));
      } else {
        setMessage({ type: 'warning', text: result.error?.message || t('wishlist.addError') });
      }
    } catch (error) {
      setMessage({ type: 'error', text: t('wishlist.addError') });
    } finally {
      setIsAdding(false);
    }
  };

  const handleAddByISBN = async (e) => {
    e.preventDefault();
    if (!isbnInput.trim()) return;

    setIsAdding(true);
    try {
      const lookupResult = await isbnApi.lookup(isbnInput.trim());

      if (lookupResult.success && lookupResult.data) {
        const { manga_id, band_id } = lookupResult.data;
        let addResult;
        if (manga_id && band_id) {
          addResult = await wishlistApi.addBand(manga_id, band_id);
        } else if (manga_id) {
          addResult = await wishlistApi.addManga(manga_id);
        } else {
          setMessage({ type: 'warning', text: t('wishlist.isbnNotInDb') });
          return;
        }
        if (addResult.success) {
          setMessage({ type: 'success', text: t('wishlist.addedViaIsbn') });
          setIsbnInput('');
          setShowAddModal(false);
          await reloadWishlist(setWishlistItems);
        } else {
          setMessage({ type: 'warning', text: addResult.error?.message || t('wishlist.addErrorIsbn') });
        }
      } else {
        setMessage({ type: 'warning', text: t('wishlist.isbnNotFound') });
      }
    } catch (error) {
      setMessage({ type: 'error', text: t('wishlist.addErrorIsbn') });
    } finally {
      setIsAdding(false);
    }
  };

  const handleAddByTitle = async (e) => {
    e.preventDefault();
    if (!titleInput.trim()) return;

    setIsAdding(true);
    try {
      // Suche nach Manga und füge zur Wishlist hinzu
      const searchResult = await searchApi.searchMangas(titleInput.trim());
      
      if (searchResult.success && searchResult.data?.items?.length > 0) {
        const manga = searchResult.data.items[0];
        const bandNumber = bandInput.trim() ? parseInt(bandInput, 10) : null;
        const addResult = bandNumber != null
          ? await wishlistApi.addBandByNumber(manga.manga_id, bandNumber)
          : await wishlistApi.addManga(manga.manga_id);
        if (addResult.success) {
          setMessage({ type: 'success', text: t('wishlist.addedToWishlist', { title: manga.title }) });
          setTitleInput('');
          setBandInput('');
          setShowAddModal(false);
          await reloadWishlist(setWishlistItems);
        } else {
          setMessage({ type: 'warning', text: addResult.error?.message || t('wishlist.addErrorTitle') });
        }
      } else {
        setMessage({ type: 'warning', text: t('wishlist.mangaNotFound') });
      }
    } catch (error) {
      setMessage({ type: 'error', text: t('wishlist.addErrorTitle') });
    } finally {
      setIsAdding(false);
    }
  };

  const handleRemoveFromWishlist = async (item) => {
    try {
      // Entferne Item via wishlist_id
      const wishlistId = item.wishlist_id || item.id;
      const result = await wishlistApi.removeItem(wishlistId);
      
      if (result.success) {
        setMessage({ type: 'success', text: t('wishlist.removedFromWishlist', { title: item.manga_title || t('wishlist.entryFallback') }) });
        await reloadWishlist(setWishlistItems);
      } else {
        setMessage({ type: 'warning', text: result.error?.message || t('wishlist.removeError') });
      }
    } catch (error) {
      setMessage({ type: 'error', text: t('wishlist.removeError') });
    }
  };

  const handleAddToShelf = async (item) => {
    try {
      let success = false;
      let errorMsg = null;

      // Pfad A: Band per manga_id + band_number (kein ISBN nötig)
      if (item.manga_id && item.band_number != null) {
        const result = await libraryApi.addBandByNumber(item.manga_id, item.band_number);
        if (result.success) {
          success = true;
        } else {
          const code = result.error?.code;
          if (code === 'ALREADY_OWNED' || code === 'CONFLICT') {
            // Bereits im Regal – trotzdem von Wunschliste entfernen
            success = true;
            errorMsg = t('wishlist.alreadyOwned', { band: item.band_number });
          } else {
            errorMsg = result.error?.message || t('wishlist.addError');
          }
        }
      }
      // Pfad B: Fallback via ISBN (falls banda_id / band_number fehlt)
      else if (item.isbn) {
        const result = await isbnApi.addToLibrary(item.isbn);
        if (result.success) {
          success = true;
        } else {
          errorMsg = result.error?.message || t('wishlist.addErrorIsbn');
        }
      } else {
        setMessage({ type: 'warning', text: t('wishlist.cannotAddNoIsbn') });
        return;
      }

      if (success) {
        // Von Wunschliste entfernen
        if (item.wishlist_id) {
          await wishlistApi.removeItem(item.wishlist_id);
        }
        await reloadWishlist(setWishlistItems);
        setMessage({
          type: 'success',
          text: errorMsg || t('wishlist.addedToShelf', {
            title: item.manga_title || t('wishlist.bandFallback'),
            bandLabel: item.band_number != null ? t('wishlist.bandLabel', { number: item.band_number }) : ''
          })
        });
      } else {
        setMessage({ type: 'warning', text: errorMsg });
      }
    } catch (error) {
      console.error('AddToShelf Error:', error);
      setMessage({ type: 'error', text: t('wishlist.addErrorShelf') });
    }
  };

  const handleReloadWishlist = async () => {
    setIsReloading(true);
    setMessage(null);

    try {
      await reloadWishlist(setWishlistItems);
      setMessage({ type: 'success', text: t('wishlist.reloadSuccess') });
    } catch (error) {
      console.error('Reload Wishlist Error:', error);
      setMessage({ type: 'error', text: t('wishlist.reloadError') });
    } finally {
      setIsReloading(false);
    }
  };

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingState}>
          <div className={styles.spinner}></div>
          <p>{t('wishlist.loadingText')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <h1>{t('wishlist.title')}</h1>
          <p>{t('wishlist.subtitle')}</p>
        </div>
        <div className={styles.headerActions}>
          <button 
            className={styles.addManualButton}
            onClick={() => setShowAddModal(true)}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path d="M12 5v14M5 12h14" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            {t('wishlist.addManual')}
          </button>
          <button 
            className={styles.reloadButton}
            onClick={handleReloadWishlist}
            disabled={isReloading}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path d="M1 4v6h6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M23 20v-6h-6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            {isReloading ? t('wishlist.reloading') : t('wishlist.reload')}
          </button>
        </div>
      </div>

      <div className={styles.searchSection}>
        <form onSubmit={handleSearch} className={styles.searchForm}>
          <div className={styles.searchInputWrapper}>
            <svg className={styles.searchIcon} width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <circle cx="11" cy="11" r="8" strokeWidth="2"/>
              <path d="M21 21l-4.35-4.35" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            <input
              type="text"
              className={styles.searchInput}
              placeholder={t('wishlist.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button 
              type="submit" 
              className={styles.searchButton}
              disabled={isSearching || !searchTerm.trim()}
            >
              {isSearching ? t('wishlist.searching') : t('wishlist.search')}
            </button>
          </div>
        </form>

        {message && (
          <div className={`${styles.message} ${styles[message.type]}`}>
            {message.text}
          </div>
        )}
      </div>

      <div className={styles.content}>
        <div className={styles.wishlistSection}>
          <div className={styles.wishlistSectionHeader}>
            <h2>{t('wishlist.myWishlist', { count: wishlistItems.length })}</h2>
            {wishlistItems.length > 0 && (
              <div className={styles.wishlistToolbar}>
                {filterOptions.length > 1 && (
                  <select
                    className={styles.sortSelect}
                    value={filterMangaTitle}
                    onChange={(e) => setFilterMangaTitle(e.target.value)}
                    aria-label={t('wishlist.filterLabel')}
                  >
                    <option value="">{t('wishlist.filterAll')}</option>
                    {filterOptions.map((title) => (
                      <option key={title} value={title}>{title}</option>
                    ))}
                  </select>
                )}
                <select
                  className={styles.sortSelect}
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  aria-label={t('wishlist.sortLabel')}
                >
                  {SORT_OPTION_KEYS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{t(opt.labelKey)}</option>
                  ))}
                </select>
                <div className={styles.viewToggle}>
                  <button
                    type="button"
                    className={`${styles.viewToggleBtn} ${viewMode === 'grid' ? styles.viewToggleBtnActive : ''}`}
                    onClick={() => setViewMode('grid')}
                    title={t('wishlist.gridView')}
                    aria-label={t('wishlist.gridView')}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>
                  </button>
                  <button
                    type="button"
                    className={`${styles.viewToggleBtn} ${viewMode === 'list' ? styles.viewToggleBtnActive : ''}`}
                    onClick={() => setViewMode('list')}
                    title={t('wishlist.listView')}
                    aria-label={t('wishlist.listView')}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="4" y1="6" x2="20" y2="6"/><line x1="4" y1="12" x2="20" y2="12"/><line x1="4" y1="18" x2="20" y2="18"/></svg>
                  </button>
                </div>
              </div>
            )}
          </div>
          {wishlistItems.length === 0 ? (
            <div className={styles.emptyState}>
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M12 2L14.5 9H22L16.5 13.5L19 20L12 15L5 20L7.5 13.5L2 9H9.5L12 2Z" strokeWidth="2" strokeLinejoin="round"/>
              </svg>
              <h3>{t('wishlist.emptyTitle')}</h3>
              <p>{t('wishlist.emptyText')}</p>
            </div>
          ) : sortedWishlistItems.length === 0 ? (
            <div className={styles.emptyState}>
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M12 2L14.5 9H22L16.5 13.5L19 20L12 15L5 20L7.5 13.5L2 9H9.5L12 2Z" strokeWidth="2" strokeLinejoin="round"/>
              </svg>
              <h3>{t('wishlist.filterEmptyTitle')}</h3>
              <p>{t('wishlist.filterEmptyText')}</p>
            </div>
          ) : viewMode === 'list' ? (
            <div className={styles.wishlistList}>
              {sortedWishlistItems.map((item, index) => (
                <div key={item.wishlist_id || index} className={styles.wishlistRow}>
                  <div className={styles.wishlistRowCoverWrap}>
                    {(item.band_cover_media_key || item.manga_cover_media_key) ? (
                      <img
                        src={getMediaUrl(item.band_cover_media_key || item.manga_cover_media_key)}
                        alt={item.manga_title}
                        className={styles.wishlistRowCover}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <div className={styles.placeholderCoverSmall}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <rect x="3" y="3" width="18" height="18" rx="2" strokeWidth="2"/>
                          <path d="M3 9h18M9 21V9" strokeWidth="2"/>
                        </svg>
                      </div>
                    )}
                  </div>

                  <div className={styles.wishlistRowInfo}>
                    <h3 title={item.band_title || item.manga_title}>{item.band_title || item.manga_title || '—'}</h3>
                    {item.band_number != null ? (
                      <span className={styles.bandNumber}>{t('wishlist.bandLabel', { number: item.band_number })}</span>
                    ) : (
                      <span className={styles.bandNumberAuto}>{t('wishlist.nextBand')}</span>
                    )}
                    {item.band_title && item.band_title !== item.manga_title && (
                      <span className={styles.bandTitleInline} title={item.manga_title}>{item.manga_title}</span>
                    )}
                  </div>

                  {item.band_id != null && (shopLinksByBand[item.band_id]?.loading || shopLinksByBand[item.band_id]?.data?.length > 0) && (
                    <div className={styles.shopButtonsRow}>
                      {shopLinksByBand[item.band_id].loading ? (
                        <span className={styles.shopBtn}>{t('wishlist.loadingShops')}</span>
                      ) : (
                        shopLinksByBand[item.band_id].data.map((shop, idx) => (
                          <a
                            key={idx}
                            href={shop.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`${styles.shopBtn} ${styles.shopBtnAccent}`}
                          >
                            🛒 {shop.name}
                          </a>
                        ))
                      )}
                    </div>
                  )}

                  <div className={styles.wishlistRowActions}>
                    <button
                      className={styles.addToShelfButton}
                      onClick={() => handleAddToShelf(item)}
                    >
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path d="M12 5v14M5 12h14" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                      {t('wishlist.addToShelf')}
                    </button>
                    <button
                      className={styles.removeButtonRow}
                      onClick={() => handleRemoveFromWishlist(item)}
                      title={t('wishlist.removeFromWishlist')}
                    >
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className={styles.wishlistGrid}>
              {sortedWishlistItems.map((item, index) => (
                <div key={item.wishlist_id || index} className={styles.wishlistCard}>
                  <div className={styles.wishlistCoverWrap}>
                    {/* Cover: erst Band-Cover, dann Manga-Cover */}
                    {(item.band_cover_media_key || item.manga_cover_media_key) ? (
                      <img
                        src={getMediaUrl(item.band_cover_media_key || item.manga_cover_media_key)}
                        alt={item.manga_title}
                        className={styles.wishlistCover}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <div className={styles.placeholderCover}>
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <rect x="3" y="3" width="18" height="18" rx="2" strokeWidth="2"/>
                          <path d="M3 9h18M9 21V9" strokeWidth="2"/>
                        </svg>
                      </div>
                    )}
                    <button
                      className={styles.removeButton}
                      onClick={() => handleRemoveFromWishlist(item)}
                      title={t('wishlist.removeFromWishlist')}
                    >
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                  </div>

                  <div className={styles.wishlistBody}>
                    <h3 title={item.band_title || item.manga_title}>{item.band_title || item.manga_title || '—'}</h3>
                    {item.band_number != null ? (
                      <span className={styles.bandNumber}>{t('wishlist.bandLabel', { number: item.band_number })}</span>
                    ) : (
                      <span className={styles.bandNumberAuto}>{t('wishlist.nextBand')}</span>
                    )}
                    {item.band_title && item.band_title !== item.manga_title && (
                      <p className={styles.bandTitle} title={item.manga_title}>{item.manga_title}</p>
                    )}

                    <button
                      className={styles.addToShelfButton}
                      onClick={() => handleAddToShelf(item)}
                    >
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path d="M12 5v14M5 12h14" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                      {t('wishlist.addToShelf')}
                    </button>

                    {item.band_id != null && (shopLinksByBand[item.band_id]?.loading || shopLinksByBand[item.band_id]?.data?.length > 0) && (
                      <div className={styles.shopButtons}>
                        {shopLinksByBand[item.band_id].loading ? (
                          <span className={styles.shopBtn}>{t('wishlist.loadingShops')}</span>
                        ) : (
                          shopLinksByBand[item.band_id].data.map((shop, idx) => (
                            <a
                              key={idx}
                              href={shop.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`${styles.shopBtn} ${styles.shopBtnAccent}`}
                            >
                              🛒 {shop.name}
                            </a>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {searchResults.length > 0 && (
          <div className={styles.resultsSection}>
            <h2>{t('wishlist.searchResults')}</h2>
            <div className={styles.resultsGrid}>
              {searchResults.map((manga, index) => {
                const mangaId = manga.manga_id || manga.id;
                const isExpanded = expandedResultId === mangaId;
                return (
                  <div key={manga.mangaId || manga.id || index} className={styles.resultCard}>
                    {(manga.cover || manga.coverUrl || manga.cover_media_key) && (
                      <img 
                        src={manga.cover?.startsWith('data:') ? manga.cover : 
                             manga.cover ? `data:image/jpeg;base64,${manga.cover}` : 
                             manga.coverUrl || getMediaUrl(manga.cover_media_key) || '/placeholder-cover.jpg'} 
                        alt={manga.title || manga.name} 
                        className={styles.resultCover} 
                      />
                    )}
                    <div className={styles.resultInfo}>
                      <h3>{manga.title || manga.name}</h3>
                      <p>{manga.author || manga.autor || manga.publisher}</p>
                    </div>
                    <div className={styles.resultActions}>
                      <button 
                        className={styles.addButton}
                        onClick={() => handleAddToWishlist(manga)}
                        disabled={isAdding}
                        title={t('wishlist.watchAllTitle')}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path d="M12 5v14M5 12h14" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                        {t('wishlist.addMangaBtn')}
                      </button>
                      <button
                        className={`${styles.addBandButton} ${isExpanded ? styles.addBandButtonActive : ''}`}
                        onClick={() => setExpandedResultId(isExpanded ? null : mangaId)}
                        title={t('wishlist.addBandTitle')}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
                        </svg>
                        {t('wishlist.addBandBtn')}
                      </button>
                    </div>
                    {isExpanded && (
                      <div className={styles.bandPickerRow}>
                        <input
                          type="number"
                          min="0"
                          className={styles.bandPickerInput}
                          placeholder={t('wishlist.bandNrPlaceholder')}
                          value={bandNumberInputs[mangaId] || ''}
                          onChange={e => setBandNumberInputs(prev => ({ ...prev, [mangaId]: e.target.value }))}
                          onKeyDown={e => {
                            if (e.key === 'Enter') handleAddBandToWishlist(manga, bandNumberInputs[mangaId] || '');
                          }}
                        />
                        <button
                          className={styles.bandPickerConfirm}
                          disabled={isAdding || !bandNumberInputs[mangaId]}
                          onClick={() => handleAddBandToWishlist(manga, bandNumberInputs[mangaId] || '')}
                        >
                          {t('wishlist.addToWishlistBtn')}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Modal für manuelles Hinzufügen */}
      {showAddModal && (
        <div className={styles.modalOverlay} onClick={() => setShowAddModal(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h3>{t('wishlist.modalTitle')}</h3>
            
            <div className={styles.tabButtons}>
              <button 
                className={`${styles.tabButton} ${addMode === 'isbn' ? styles.active : ''}`}
                onClick={() => setAddMode('isbn')}
              >
                {t('wishlist.viaIsbn')}
              </button>
              <button 
                className={`${styles.tabButton} ${addMode === 'title' ? styles.active : ''}`}
                onClick={() => setAddMode('title')}
              >
                {t('wishlist.viaTitle')}
              </button>
            </div>

            {addMode === 'isbn' ? (
              <form onSubmit={handleAddByISBN} className={styles.addForm}>
                <div className={styles.formGroup}>
                  <label>{t('wishlist.isbnLabel')}</label>
                  <input
                    type="text"
                    value={isbnInput}
                    onChange={(e) => setIsbnInput(e.target.value)}
                    placeholder={t('wishlist.isbnPlaceholder')}
                    required
                  />
                  <span className={styles.hint}>{t('wishlist.isbnHint')}</span>
                </div>
                <div className={styles.modalActions}>
                  <button type="button" onClick={() => setShowAddModal(false)}>{t('wishlist.cancel')}</button>
                  <button type="submit" disabled={isAdding || !isbnInput.trim()}>
                    {isAdding ? t('wishlist.adding') : t('wishlist.add')}
                  </button>
                </div>
              </form>
            ) : (
              <form onSubmit={handleAddByTitle} className={styles.addForm}>
                <div className={styles.formGroup}>
                  <label>{t('wishlist.titleLabel')}</label>
                  <input
                    type="text"
                    value={titleInput}
                    onChange={(e) => setTitleInput(e.target.value)}
                    placeholder={t('wishlist.titlePlaceholder')}
                    required
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>{t('wishlist.bandNumberLabel')}</label>
                  <input
                    type="number"
                    value={bandInput}
                    onChange={(e) => setBandInput(e.target.value)}
                    placeholder={t('wishlist.bandNumberPlaceholder')}
                    min="0"
                    required
                  />
                </div>
                <div className={styles.modalActions}>
                  <button type="button" onClick={() => setShowAddModal(false)}>{t('wishlist.cancel')}</button>
                  <button type="submit" disabled={isAdding || !titleInput.trim() || !bandInput}>
                    {isAdding ? t('wishlist.adding') : t('wishlist.add')}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default Wishlist;
